import { useEffect, useState } from 'react';
import { api, type Brand } from '../lib/api';

// What viewers ask: every research question about this workspace's shows and
// episodes, most asked first, and whether the catalogue could answer. The
// assistant director's best source of a next episode: a question the door
// could not answer is one the audience is already waiting for.
export function ViewerQuestions({ onStarted }: { onStarted?: (cardId: string) => void }) {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [qs, setQs] = useState<{ question: string; scope: string; slug: string | null; hits: number; answerable: boolean; askedAt: string }[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, { answerable: boolean; answer: string; citations: { slug: string; title: string; at: number; quote: string }[] }>>({});
  const [status, setStatus] = useState<{ shows: { slug: string | null }[] } | null>(null);
  useEffect(() => { api.doorStatus().then((d) => setStatus({ shows: d.shows })).catch(() => undefined); }, []);
  const ask = async (q: { question: string; scope: string }) => {
    const creator = q.scope.startsWith('creator:') ? q.scope.slice(8) : status?.shows.find((b) => b.slug)?.slug ?? null; if (!creator) return;
    setBusy(q.question);
    try { const a = await api.doorAnswer(q.question, creator); setAnswers((m) => ({ ...m, [q.question]: a })); } catch (e) { setError((e as Error).message); } finally { setBusy(null); }
  };
  useEffect(() => { api.doorQuestions().then(setQs).catch((e: Error) => setError(e.message)); api.brands().then(setBrands).catch(() => undefined); }, []);
  if (qs && qs.length === 0) return null;
  const start = async (q: string) => {
    setBusy(q); setError(null);
    try {
      const card = await api.createCard({ title: q.length > 80 ? q.slice(0, 77) + '…' : q, brandId: brands[0]?.id ?? null, painPoint: `Viewers asked: "${q}" — and the catalogue could not answer it yet.` });
      onStarted?.(card.id);
    } catch (e) { setError((e as Error).message); } finally { setBusy(null); }
  };
  return (
    <section className="card" data-tool="viewer-questions">
      <h3>What viewers ask</h3>
      <p className="sub-dim">Questions people asked the door about your shows and episodes, most asked first. One the catalogue could not answer is an episode waiting to be made.</p>
      {error && <div className="err inline">{error}</div>}
      {!qs ? <p className="sub-dim">Loading…</p> : (
        <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0', display: 'grid', gap: 6, fontSize: 13.5 }}>
          {qs.slice(0, 20).map((q) => (
            <li key={q.scope + q.question} style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
              <span className="chip" title="Times asked">{q.hits}×</span>
              <span style={{ flex: 1 }}>{q.question} <span className="sub-dim">· {q.answerable ? 'answered from your episodes' : 'not answered yet'}{q.slug ? ` · about ${q.slug}` : ''}</span></span>
              <button type="button" className="btn tiny" disabled={busy !== null} onClick={() => void ask(q)} title="What the door would answer today, from your episodes">What the door says</button>
              {!q.answerable && <button type="button" className="btn tiny" disabled={busy !== null} onClick={() => void start(q.question)}>Start an episode from this</button>}
            </li>
          )).flatMap((li, i) => { const q = qs[i]!; const a = answers[q.question]; return a ? [li, (
            <li key={q.question + '-answer'} className="sub-dim" style={{ marginLeft: 36, fontSize: 13 }} data-tool="door-answer">
              {a.answer}{a.citations.length ? <> · {a.citations.map((c) => <span key={c.slug + c.at}>“{c.quote}” <i>({c.title})</i> </span>)}</> : null}
            </li>)] : [li]; })}
        </ul>
      )}
    </section>
  );
}
